import {
  AlertTriangle,
  ArrowDown,
  Bot,
  CircleDot,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { ReviewVerdict } from "@/components/cinch/ReviewVerdict";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useReviewParentChild,
  type ParentChildItem,
} from "../api";

/** Review quick-view for one parent/child relationship change: what was asked
 *  for, what happened to it in the close month, the parent group's AI verdict,
 *  and the reviewer's own verdict + notes.
 *
 *  Opens from a row click or the row's flag button; URL state (`?link=&expanded=`)
 *  is owned by the parent panel. */
export function ParentChildDrawer({
  period,
  item,
  canAct,
  locked,
  expanded,
  presetFlag,
  onExpandedChange,
  onClose,
}: {
  period: string;
  item: ParentChildItem | null;
  canAct: boolean;
  locked: boolean;
  expanded: boolean;
  /** True when opened via a row's Flag button — preselects the flagged verdict
   *  so the reviewer lands straight in the notes box. */
  presetFlag: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const review = useReviewParentChild(period);

  return (
    <EntityDrawer
      open={item != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={item ? (item.child_name ?? item.child_company_id) : ""}
      subtitle={
        item
          ? `${item.action_label} · ${item.stage_label} in ${monthLabel(period)}`
          : undefined
      }
      onClose={onClose}
    >
      {item && (
        <div className="flex flex-col gap-5 p-4">
          {/* The change itself, as a tree fragment — the shape being asserted. */}
          <div className="space-y-3 rounded-md border px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                The change
              </span>
              <StatusBadge
                value={item.stage === "verified" ? "approved" : item.stage}
                label={item.stage_label}
              />
            </div>
            <RelationshipLine item={item} />
            {item.reason && (
              <p className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Reason given
                </span>
                <span className="mt-0.5 block">{item.reason}</span>
              </p>
            )}
          </div>

          {/* Approved is a decision; the tree only changes when Cinch confirms
              it. Cinch has no write API for company relationships, so this gap
              is a normal state, not an error — but it's the state that matters
              most to a roll-up, so it gets its own callout. */}
          {item.awaiting_apply && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-light/50 px-3 py-2.5 text-sm text-warning">
              <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" />
              <div>
                <b>Approved, not yet live in Cinch.</b> Company relationships
                have no write API — someone has to apply this in the Cinch admin
                and then verify it. Until that lands, every roll-up still reads
                the old tree.
                {item.apply_error && (
                  <span className="mt-1 block text-xs">{item.apply_error}</span>
                )}
              </div>
            </div>
          )}

          {/* What happened, in the close month only. June's events belong to
              June's close — the same request is reviewed once per month it
              moved in. */}
          <div className="space-y-2">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> This month's activity
            </span>
            <ol className="divide-y divide-border rounded-md border text-sm">
              {item.events.map((e, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2 px-3 py-2">
                  <CircleDot
                    className={cn(
                      "mt-1 h-3 w-3 flex-shrink-0",
                      e.event === "applied_verified"
                        ? "text-success"
                        : e.event === "rejected"
                          ? "text-danger"
                          : "text-muted-foreground",
                    )}
                  />
                  <span className="font-medium">{EVENT_LABEL[e.event] ?? e.event}</span>
                  {e.actor_name && (
                    <span className="text-muted-foreground">by {e.actor_name}</span>
                  )}
                  {e.at && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(e.at).toLocaleString()}
                    </span>
                  )}
                  {e.detail && (
                    <span className="basis-full pl-5 text-xs text-muted-foreground">
                      “{e.detail}”
                    </span>
                  )}
                </li>
              ))}
              {item.events.length === 0 && (
                <li className="px-3 py-2 text-muted-foreground">
                  No workflow events in {monthLabel(period)}.
                </li>
              )}
            </ol>
            {item.current_status && (
              <p className="text-xs text-muted-foreground">
                The request's state today:{" "}
                <StatusBadge value={item.current_status} />
                {item.applied_at
                  ? ` · live in Cinch since ${new Date(item.applied_at).toLocaleDateString()}`
                  : ""}
              </p>
            )}
          </div>

          {/* The AI's read of the whole parent group. Context, never a gate —
              it reviews the group, not this one change. */}
          {item.ai_status && (
            <div className="space-y-1.5 rounded-md border px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Bot className="h-3.5 w-3.5" /> AI review ·{" "}
                  {item.new_parent_name ?? item.parent_name ?? "parent group"}
                </span>
                <StatusBadge value={item.ai_status} />
              </div>
              {item.ai_summary && <p className="text-sm">{item.ai_summary}</p>}
              <p className="text-xs text-muted-foreground">
                The model looked at the whole group, not just this change — treat
                it as a prompt to look closer, not a verdict.
              </p>
            </div>
          )}

          {/* The reviewer's verdict. "Verified" rather than "Approved": the
              question is whether the child really belongs under the parent,
              not whether the request should have been granted. */}
          <ReviewVerdict
            itemKey={item.item_key}
            status={item.review_status}
            statusLabel={item.review_status === "approved" ? "Verified" : undefined}
            notes={item.review_notes}
            reviewedByName={item.reviewed_by_name}
            reviewedAt={item.reviewed_at}
            canAct={canAct}
            saving={review.isPending}
            presetFlag={presetFlag}
            approveLabel="Verified"
            flagPlaceholder="What's wrong with this relationship? (context for whoever fixes the tree)"
            deniedReason={
              locked
                ? "R-16 is signed off — an admin must unlock it to change a verdict."
                : "Only an R-16 owner (RevOps / Partner) can record a verdict."
            }
            onSave={({ status, notes }) =>
              review.mutate(
                { itemKey: item.item_key, status, notes },
                {
                  onSuccess: () =>
                    toast.success(
                      status === "flagged"
                        ? `${item.child_name ?? "Relationship"} flagged`
                        : status === "approved"
                          ? `${item.child_name ?? "Relationship"} verified`
                          : `${item.child_name ?? "Relationship"} reset to pending`,
                    ),
                  onError: (e) =>
                    toast.error("Couldn't save review", {
                      description: (e as Error).message,
                    }),
                },
              )
            }
          />
        </div>
      )}
    </EntityDrawer>
  );
}

const EVENT_LABEL: Record<string, string> = {
  request_created: "Requested",
  approved: "Approved by a second reviewer",
  applied_verified: "Verified live in Cinch",
  rejected: "Rejected",
  cancelled: "Withdrawn by the requester",
};

/** The tree fragment under review, stacked parent-over-child.
 *
 *  Vertical rather than `parent → child` on one line: the drawer is a narrow
 *  column, and a horizontal arrow wraps to the END of the parent's line, where
 *  it reads as pointing at something off-screen instead of at the child. A
 *  move chains a third row so the reader can see where the child landed. */
function RelationshipLine({ item }: { item: ParentChildItem }) {
  const moved = item.action === "move" && !!item.new_parent_company_id;
  return (
    <div className="text-sm">
      <Company
        name={item.parent_name}
        id={item.parent_company_id}
        side="parent"
        label="Parent"
      />
      <Connector
        note={
          item.action === "remove" ? "unlinked" : moved ? "moved off" : undefined
        }
      />
      <Company
        name={item.child_name}
        id={item.child_company_id}
        side="child"
        label="Child"
      />
      {moved && (
        <>
          <Connector note="now under" />
          <Company
            name={item.new_parent_name}
            id={item.new_parent_company_id ?? ""}
            side="parent"
            label="New parent"
          />
        </>
      )}
    </div>
  );
}

/** The link between two rows, carrying what happened to it. */
function Connector({ note }: { note?: string }) {
  return (
    <div className="flex items-center gap-1.5 py-1 pl-1">
      <ArrowDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      {note && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {note}
        </span>
      )}
    </div>
  );
}

function Company({
  name,
  id,
  side,
  label,
}: {
  name: string | null;
  id: string;
  /** Which end of the relationship this is — the parent reads heavier. NOT
   *  `role`, which the a11y lint takes for an ARIA attribute. */
  side: "parent" | "child";
  label: string;
}) {
  return (
    <div className="min-w-0">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "block truncate",
          side === "parent" ? "font-semibold" : "font-medium",
        )}
      >
        {name ?? "—"}
      </span>
      <span className="block truncate font-mono text-[10px] text-muted-foreground">
        {id || "—"}
      </span>
    </div>
  );
}
